import React from 'react';
import { RouterProvider as AriaProvider } from 'react-aria-components';
import { useHref, useNavigate } from 'react-router';

type AriaRouterProviderProps = {
  children: React.ReactNode;
};

// Any href carrying a scheme (https:, mailto:, tel:, ...) or protocol-relative
// prefix addresses something outside the router.
const hasScheme = (href: string) => /^[a-z][a-z0-9+.-]*:|^\/\//i.test(href);

/**
 * React Router's `useHref` resolves its argument as a ROUTE PATH, so handing
 * it an absolute URL mangles it — `mailto:a@b.com` becomes `/mailto:a@b.com`
 * and `https://x.com/y` becomes `/https:/x.com/y`. React Aria calls useHref
 * for every href it renders, so passing the router's hook unguarded breaks
 * every external, mailto: and tel: link on the site.
 *
 * Resolve internal paths through the router (which keeps a future `basename`
 * working) and pass anything with a scheme through untouched. The router hook
 * is still called unconditionally, so the rules of hooks hold.
 */
function useSafeHref(href: string) {
  const routerHref = useHref(href);
  return hasScheme(href) ? href : routerHref;
}

/**
 * Routes React Aria's `href` components through React Router.
 *
 * Every React Aria component that takes an `href` (Link, LinkButton, menu
 * items, ...) does a native browser navigation by default — a full page load.
 * This hands React Aria the router's `navigate`, so internal hrefs become
 * client-side transitions. Cross-origin hrefs are left alone and still use
 * native navigation, which is what external links want.
 *
 * Must render INSIDE the route tree: `useNavigate` needs router context, so
 * this cannot wrap React Router's own `<RouterProvider>`. It sits in the root
 * route's element instead — see app.tsx.
 *
 * `useHref` is passed so React Aria renders the same resolved URLs the router
 * would, which keeps a future `basename` working without touching call sites —
 * but only for internal paths. See useSafeHref above.
 */
export function AriaRouterProvider({ children }: AriaRouterProviderProps) {
  const navigate = useNavigate();

  return (
    <AriaProvider navigate={navigate} useHref={useSafeHref}>
      {children}
    </AriaProvider>
  );
}
